<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="プロセス手順生成" v-if="isEditable">
        <v-btn icon :disabled="!processImplementation" @click="openCreateDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </el-tooltip>
    </template>

    <el-table
        :data="processOperationImplementationsView"
        :height="tableHeight"
        border>
      <el-table-column
          prop="dataImplementation.name"
          label="データ実装">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.dataImplementation && row.dataImplementation.dataComponentModel && row.dataImplementation.dataComponentModel.name"
                :value-local-id="row.dataImplementation && row.dataImplementation.localId">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          v-for="pot in processOperationTypeItems"
          :key="pot.name"
          :label="pot.label"
          :prop="pot.name"
          width="50"
          align="center">
        <template #default="{row}">
          <table-inline>
            <readonly-checkbox :value="row.operationTypes.includes(pot.name)"></readonly-checkbox>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="95"
          align="center"
          fixed="right">
        <template #default="{row}">
          <icon-btns>
            <!-- 編集モード -->
            <template v-if="isEditable">
              <icon-btn
                  type="delete"
                  @click="openDeleteDialog(row)">
              </icon-btn>
              <icon-btn
                  type="edit-content"
                  @click="openUpdateDialog(row)">
              </icon-btn>
            </template>
          </icon-btns>
        </template>
      </el-table-column>

      <template #empty>
        <v-row
            v-if="processOperationImplementationsLoading"
            justify="center"
            class="pa-10">
          <v-progress-circular indeterminate/>
        </v-row>
        <v-row
            v-else-if="processImplementation == null"
            justify="center"
            class="pa-10">
          プロセス実装を選択してください
        </v-row>
      </template>
    </el-table>

    <!-- 作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreate.visible"
        title-text="プロセス手順実装作成"
        @close="closeCreateDialog">
      <data-implementation-select-form
          v-model="dialogCreate.dataImplementationId"
          :data-implementations="dataImplementations"
          :service-implementation="serviceImplementation"
          :process-implementation="processImplementation"
          @change="dialogCreate.operationTypes = dialogCreate.operationTypesInitial = collectOperationTypes($event)">
        <template #append="{dataImplementation}">
          <v-row>
            <v-col cols="12">
              <process-operation-type-select-form
                  v-model="dialogCreate.operationTypes"
                  :process-component-model-id="processImplementation.processComponentModelId"
                  :data-component-model-id="dataImplementation && dataImplementation.dataComponentModelId"
                  :disabled="!dialogCreate.dataImplementationId">
              </process-operation-type-select-form>
            </v-col>
          </v-row>
        </template>
      </data-implementation-select-form>
    </simple-dialog>

    <!-- 編集ダイアログ -->
    <simple-dialog
        v-model="dialogUpdate.visible"
        title-text="プロセス手順実装編集"
        @close="closeUpdateDialog">
      <data-implementation-select-form
          v-model="dialogUpdate.dataImplementationId"
          :data-implementations="dataImplementations"
          :service-implementation="serviceImplementation"
          :process-implementation="processImplementation"
          readonly>
        <template #append="{dataImplementation}">
          <v-row>
            <v-col cols="12">
              <process-operation-type-select-form
                  v-model="dialogUpdate.operationTypes"
                  :process-component-model-id="processImplementation.processComponentModelId"
                  :data-component-model-id="dataImplementation && dataImplementation.dataComponentModelId"
                  :disabled="!dialogUpdate.dataImplementationId">
              </process-operation-type-select-form>
            </v-col>
          </v-row>
        </template>
      </data-implementation-select-form>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
          v-model="dialogDelete.visible"
          title-text="プロセス手順実装削除"
          title-color="delete"
          apply-text="削除"
          header-text="以下のプロセス手順実装を削除します。本当によろしいですか？"
          @close="closeDeleteDialog">
      <data-implementation-select-form
          v-model="dialogDelete.dataImplementationId"
          :data-implementations="dataImplementations"
          :service-implementation="serviceImplementation"
          :process-implementation="processImplementation"
          readonly>
        <template #append>
          <v-row>
            <v-col cols="12">
              <text-field-like
                  label="区分"
                  :value="
                    Object.entries(operationTypes)
                      .map(([name, label]) => dialogDelete.operationTypes.includes(name) && label)
                      .filter(label => !!label)
                      .join(', ')"
                  :messages="`${dialogDelete.operationTypes.length} 件`">
              </text-field-like>
            </v-col>
          </v-row>
        </template>
      </data-implementation-select-form>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import {mapGetters, mapMutations, mapState} from "vuex";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import PathInfoMixin from "@/router/PathInfoMixin";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import { PROCESS_OPERATION_OPERATION_TYPES } from "@/constants";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import BetterSet from "betterset";
import groupArray from "group-array";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import IoablePageMixin from "@/router/IoablePageMixin";
import DataImplementationSelectForm
  from "@/components/pages/implementation/ServiceImplementationShow/DataImplementationSelectForm";
import ProcessOperationTypeSelectForm
  from "@/components/pages/implementation/DataImplementationShow/ProcessOperationTypeSelectForm";

export default {
  name: 'ProcessOperationImplementationTable',
  mixins: [
    PathInfoMixin,
    IoablePageMixin
  ],
  components: {
    ProcessOperationTypeSelectForm,
    DataImplementationSelectForm,
    ReadonlyCheckbox,
    TextFieldLike,
    TableInline,
    IconBtn,
    IconBtns,
    TabItemTemplate,
    SimpleDialog,
  },
  props: {
    serviceImplementation: {
      type: Object,
      required: true
    },
    processImplementation: {
      type: Object,
      default: null
    },
    height: {
      type: Number,
    },
  },
  data() {
    return {
      processOperationTypeItems: ['create', 'read', 'update', 'delete'].map(t => ({
        name: t,
        label: PROCESS_OPERATION_OPERATION_TYPES[t],
      })),
      processOperationImplementationsLoading: false,
      dataImplementations: [],
      dialogCreate: {
        visible: false,
        dataImplementationId: null,
        operationTypesInitial: [],
        operationTypes: []
      },
      dialogUpdate: {
        visible: false,
        dataImplementationId: null,
        operationTypesInitial: [],
        operationTypes: []
      },
      dialogDelete: {
        visible: false,
        dataImplementationId: null,
        operationTypes: []
      },
    }
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    ...mapState('hcs/processOperationImplementation', ['processOperationImplementations']),
    operationTypes: () => PROCESS_OPERATION_OPERATION_TYPES,
    processOperationImplementationsView() {
      return Object.values(groupArray(this.processOperationImplementations, poi => poi.dataImplementationId)).map(group => {
        const first = group[0];
        if(first === undefined) {
          return undefined;
        }
        return {
          ...first,
          id: undefined,
          index: undefined,
          createdAt: undefined,
          updatedAt: undefined,
          operationType: undefined,
          operationTypes: this.collectOperationTypes(first.dataImplementationId),
          dataImplementation: this.dataImplementations.find(di => di.id === first.dataImplementationId)
        };
      }).filter(poi => !!poi);
    },
    tableHeight() {
      return this.height ? Math.max(0, this.height - 48) : undefined;
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    async load() {
      await this.loadDataImplementations();
    },
    async loadDataImplementations() {
      const {data: dataImplementations} = await this.hcsClient.get('/data_implementations', {
        params: {
          'q[controller_id_eq]': this.serviceImplementation.controllerId
        }
      });
      this.dataImplementations = dataImplementations;
    },
    async loadProcessOperationImplementations() {
      this.$store.commit('hcs/processOperationImplementation/setProcessOperationImplementations', []);
      if(this.processImplementation === null) {
        return;
      }

      try {
        this.processOperationImplementationsLoading = true;
        await this.$store.dispatch('hcs/processOperationImplementation/findList', {
          'q[process_implementation_id_eq]': this.processImplementation.id
        });
      } finally {
        this.processOperationImplementationsLoading = false;
      }
    },
    openCreateDialog() {
      this.dialogCreate = {
        visible: true,
        dataImplementationId: null,
        operationTypesInitial: [],
        operationTypes: []
      };
    },
    async closeCreateDialog(apply) {
      this.dialogCreate.visible = false;
      if(apply) {
        this.showSnackbar({
          message: 'プロセス手順を更新中...',
          timeout: false,
          loading: true
        });

        try {
          await this.updateProcessOperationImplementations(this.dialogCreate);
          this.$store.commit({
            type: 'showSnackbar',
            message: `プロセス手順が更新されました`
          });
        } catch (e) {
          console.error(e);
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `プロセス手順の更新に失敗しました`
          });
        }
      }
    },
    openUpdateDialog(row) {
      this.dialogUpdate = {
        visible: true,
        dataImplementationId: row.dataImplementationId,
        operationTypesInitial: row.operationTypes,
        operationTypes: row.operationTypes
      };
    },
    async closeUpdateDialog(apply) {
      this.dialogUpdate.visible = false
      if(apply) {
        this.showSnackbar({
          message: 'プロセス手順を更新中...',
          timeout: false,
          loading: true
        });

        try {
          await this.updateProcessOperationImplementations(this.dialogUpdate);
          this.$store.commit({
            type: 'showSnackbar',
            message: `プロセス手順が更新されました`
          });
        } catch (e) {
          console.error(e);
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `プロセス手順の更新に失敗しました`
          });
        }
      }
    },
    openDeleteDialog(row) {
      this.dialogDelete = {
        visible: true,
        dataImplementationId: row.dataImplementationId,
        operationTypes: row.operationTypes
      };
    },
    async closeDeleteDialog(apply) {
      this.dialogDelete.visible = false;
      if(apply) {
        this.showSnackbar({
          message: 'プロセス手順を削除中...',
          timeout: false,
          loading: true
        });
        try {
          for(const operationType of this.dialogDelete.operationTypes) {
            const poi = this.processOperationImplementations.find(poi =>
                poi.dataImplementationId === this.dialogDelete.dataImplementationId &&
                poi.operationType === operationType);
            if(poi) {
              await this.$store.dispatch('hcs/processOperationImplementation/delete', poi.id);
            }
          }
          this.$store.commit({
            type: 'showSnackbar',
            message: `プロセス手順が削除されました`
          });
        } catch (e) {
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `プロセス手順の削除に失敗しました`
          });
        }
      }
    },
    /**
     * 指定されたデータ実装IDに関連するプロセス手順実装の区分を収集します
     *
     * @param {string} dataImplementationId データ実装ID
     * @return {(keyof typeof PROCESS_OPERATION_OPERATION_TYPES)[]} プロセス手順実装の区分の配列
     */
    collectOperationTypes(dataImplementationId) {
      const result = new BetterSet();
      this.processOperationImplementations.forEach(poi => {
        if(poi.dataImplementationId === dataImplementationId) {
          result.add(poi.operationType);
        }
      });
      return result.asArray();
    },
    /**
     * 新旧のプロセス手順実装の区分の配列の差分を取得します
     *
     * @param {string[]} oldTypes 旧区分の配列
     * @param {string[]} newTypes 新区分の配列
     * @return {{create: string[], remove: string[]}} 配列の差分
     */
    diffOperationTypes(oldTypes, newTypes) {
      const oldTypeSet = new BetterSet(oldTypes);
      const newTypeSet = new BetterSet(newTypes);
      const intersection = oldTypeSet.intersection(newTypeSet);
      return {
        create: newTypeSet.relativeComplement(intersection).asArray(),
        remove: oldTypeSet.relativeComplement(intersection).asArray()
      };
    },
    /**+
     * プロセス手順実装を更新します
     *
     * @param {{
     *   dataImplementationId: string,
     *   operationTypesInitial: (keyof typeof PROCESS_OPERATION_OPERATION_TYPES)[],
     *   operationTypes: (keyof typeof PROCESS_OPERATION_OPERATION_TYPES)[],
     * }} form 更新データ
     * @return {Promise<void>}
     */
    async updateProcessOperationImplementations(form) {
      const dataImplementationId = form.dataImplementationId;
      const otDiff = this.diffOperationTypes(form.operationTypesInitial, form.operationTypes);

      let currentIndex = this.processOperationImplementations
          .filter(poi => poi.dataImplementationId === dataImplementationId)
          .map(poi => poi.index)
          .reduce((a, b) => Math.max(a, b), 1);

      const removePois = otDiff.remove
          .flatMap(operationType => this.processOperationImplementations
              .filter(poi => poi.dataImplementationId === dataImplementationId && poi.operationType === operationType));

      const createPois = otDiff.create.map(operationType => ({
        processImplementationId: this.processImplementation.id,
        dataImplementationId: dataImplementationId,
        index: ++currentIndex,
        operationType: operationType
      }));

      for(const removePoi of removePois) {
        await this.$store.dispatch('hcs/processOperationImplementation/delete', removePoi.id);
      }

      for(const createPoi of createPois) {
        await this.$store.dispatch('hcs/processOperationImplementation/create', createPoi);
      }
    },
  },
  watch: {
    processImplementation: {
      immediate: true,
      handler() {
        this.loadProcessOperationImplementations();
      }
    }
  }
}
</script>
